import { makeKindKeyStringFromKindKey } from '@gonfalon/contexts/kind-key/makeKindKeyString';
import { ContextKindKey } from '@gonfalon/contexts/types';
import http, { middleware } from '@gonfalon/legacy-http-client/httpUtils';
import type { schemas } from '@gonfalon/openapi/types';
import type { Queryfilter as QueryFilter } from '@gonfalon/queryfilter/builder';
import {
  and,
  anyOf,
  equals,
  exists,
  or,
  parenthesized,
  toString as queryfilterToString,
  startsWith,
} from '@gonfalon/queryfilter/builder';
import type { ContextKindsCollectionRep } from '@gonfalon/rest-api/types';

import type { ContextAttributesResponse, ContextTargetKindKey } from '../types';
import { augmentResolveContextsResponse } from '../utils/contextTargetingUtils';

export const fetchContextList = (projectKey: string, environmentKey: string, kind: string, prefix: string) => {
  const url = new URL(
    `/api/v2/projects/${projectKey}/environments/${environmentKey}/contexts/search`,
    window.location.origin,
  );
  const searchParams = new URLSearchParams();
  const matches: QueryFilter[] = [];
  matches.push(equals('kind', kind));

  if (prefix) {
    let orList = [startsWith('*./name', prefix), startsWith('key', prefix)];
    if (kind === ContextKindKey.USER) {
      orList = [
        ...orList,
        startsWith('user.lastName', prefix),
        startsWith('user.firstName', prefix),
        startsWith('user.email', prefix),
      ];
    }
    matches.push(parenthesized(or(orList)));
  }

  searchParams.append('filter', encodeURIComponent(queryfilterToString(and(matches))));
  searchParams.append('limit', '20');
  searchParams.append('sort', '-ts');
  url.search = searchParams.toString();

  return http.post(url.toString()).then(middleware.json).catch(middleware.jsonError);
};

export const fetchContextAttributesByKind = (
  projectKey: string,
  environmentKey: string,
  kind: string,
  prefix?: string,
): Promise<ContextAttributesResponse> => {
  const url = new URL(
    `/api/v2/projects/${projectKey}/environments/${environmentKey}/context-attributes`,
    window.location.origin,
  );
  const searchParams = new URLSearchParams();
  const matches: QueryFilter[] = [];
  if (kind) {
    matches.push(equals('kind', kind));
  }
  if (prefix) {
    matches.push(startsWith('name', prefix));
  }
  searchParams.append('filter', encodeURIComponent(queryfilterToString(and(matches))));
  url.search = searchParams.toString();

  return http.get(url.toString()).then(middleware.json).catch(middleware.jsonError);
};

export const fetchContextAttributeValues = (
  projectKey: string,
  environmentKey: string,
  kind: string,
  attributeKey: string,
  prefix?: string,
) => {
  const url = new URL(
    `/api/v2/projects/${projectKey}/environments/${environmentKey}/context-attributes/${encodeURIComponent(
      attributeKey,
    )}`,
    window.location.origin,
  );
  const searchParams = new URLSearchParams();
  const matches: QueryFilter[] = [];
  matches.push(equals('kind', kind));
  if (prefix) {
    matches.push(startsWith('value', prefix));
  }
  searchParams.append('filter', encodeURIComponent(queryfilterToString(and(matches))));
  searchParams.append('limit', '100');
  url.search = searchParams.toString();
  return http.get(url.toString()).then(middleware.json).catch(middleware.jsonError);
};

export const resolveContexts = (
  projectKey: string,
  environmentKey: string,
  contextTargetKindKeysOrEmails: ContextTargetKindKey[],
  sort?: string,
  searchEmails: boolean = false,
) => {
  const url = new URL(
    `/api/v2/projects/${projectKey}/environments/${environmentKey}/contexts/search`,
    window.location.origin,
  );
  const matches: QueryFilter[] = [];
  const searchParams = new URLSearchParams();
  if (!contextTargetKindKeysOrEmails.length) {
    return Promise.resolve([]);
  }

  const kindKeyFilter = anyOf('kindkey', contextTargetKindKeysOrEmails.map(makeKindKeyStringFromKindKey));

  const hasAllUserKindKeys = contextTargetKindKeysOrEmails.every((el) => el.kind === ContextKindKey.USER);
  if (searchEmails && hasAllUserKindKeys) {
    const emailFilters = contextTargetKindKeysOrEmails.map((el) => equals('user.email', el.key));
    matches.push(or([kindKeyFilter, ...emailFilters]));
  } else {
    matches.push(kindKeyFilter);
  }

  const filter = queryfilterToString(and(matches));
  searchParams.append('filter', encodeURIComponent(filter));
  searchParams.append('limit', contextTargetKindKeysOrEmails.length.toString());
  searchParams.append('sort', sort || '-ts');
  url.search = searchParams.toString();

  return http
    .post(url.toString())
    .then(middleware.json)
    .then((data) => augmentResolveContextsResponse(data))
    .catch(middleware.jsonError);
};

export type ContextKindRequest = schemas['UpsertContextKindPayload'];

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(() => resolve(), ms));
export const updateContextKindKey = async (
  projectKey: string,
  contextKindKey: string,
  contextKindRequest: ContextKindRequest,
) => {
  const updateResponse = await http
    .put(`/api/v2/projects/${projectKey}/context-kinds/${contextKindKey}`, {
      body: JSON.stringify(contextKindRequest),
      beta: true,
    })
    .then(middleware.json)
    .catch(middleware.jsonError);

  let waiting = true;

  const pollingTimeout = setTimeout(() => {
    waiting = false;
  }, 60_000);

  // Despite the PUT returning a success, the next GET returns stale data
  // This loop ensures that the promise resolves only after we get a confirmation of the write
  // from the API. It will timeout and assume success after 1 minute to avoid hammering the API.
  while (waiting) {
    const verifyApiResponse: ContextKindsCollectionRep = await http
      .get(`/api/v2/projects/${projectKey}/context-kinds`)
      .then((r) => r.json());

    const matchingContextKind = verifyApiResponse.items.find((item) => item.key === contextKindKey);
    if (!matchingContextKind) {
      await wait(1000);
      continue;
    }
    if (contextKindRequest.archived !== undefined) {
      // matchingContextKind?.archived comes back as `undefined` if false
      if (contextKindRequest.archived !== !!matchingContextKind?.archived) {
        await wait(1000);
        continue;
      }
    }

    if (contextKindRequest.description !== undefined) {
      if (contextKindRequest.description !== matchingContextKind?.description) {
        await wait(1000);
        continue;
      }
    }

    if (contextKindRequest.name !== undefined) {
      if (contextKindRequest.name !== matchingContextKind?.name) {
        await wait(1000);
        continue;
      }
    }
    if (contextKindRequest.version !== undefined) {
      if (contextKindRequest.version !== matchingContextKind?.version) {
        await wait(1000);
        continue;
      }
    }

    // All checks passed
    break;
  }

  clearTimeout(pollingTimeout);

  return updateResponse;
};
export const fetchContextsWithNames = (projectKey: string, environmentKey: string, missingNamesKindKeys: string[]) => {
  const url = new URL(
    `/api/v2/projects/${projectKey}/environments/${environmentKey}/contexts/search`,
    window.location.origin,
  );
  const matches: QueryFilter[] = [];
  matches.push(anyOf('kindkey', missingNamesKindKeys));
  matches.push(exists('*.name', true));

  const filter = queryfilterToString(and(matches));

  const searchParams = new URLSearchParams();
  searchParams.set('includeTotalCount', String(true));
  url.search = searchParams.toString();

  return http
    .post(url.toString(), {
      body: {
        filter,
        sort: '-ts',
      },
    })
    .then(middleware.json)
    .catch(middleware.jsonError);
};
